/**
 * Shared URL helpers for list pages (`/work-items`, `/documents`, …) that
 * store sort + multi-value facet state in `URLSearchParams`. All list pages
 * use the same encoding so URLs round-trip cleanly and can be linked
 * cross-page in tooling.
 */

import type { SortDescriptor } from '../../components/Table';

/**
 * Decode a comma-separated `?facet=a,b,c` string into an array. Empty / null
 * inputs return `[]`. Values are trimmed; empties are dropped.
 */
export function parseListParam(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Decode a `?sort=-col` (descending) or `?sort=col` (ascending) string into a
 * `SortDescriptor`. Returns null when the input is missing or malformed.
 */
export function parseSortParam(raw: string | null): SortDescriptor | null {
  if (!raw) return null;
  const desc = raw.startsWith('-');
  const key = desc ? raw.slice(1) : raw;
  if (!key) return null;
  return { columnKey: key, direction: desc ? 'desc' : 'asc' };
}

/**
 * Encode a `SortDescriptor` back into the `?sort=` string. Descending sorts
 * get a leading `-`; ascending sorts are bare column keys. `undefined` input
 * returns null so the caller can `delete` the param.
 */
export function encodeSortParam(s: SortDescriptor | undefined): string | null {
  if (!s) return null;
  return s.direction === 'desc' ? `-${s.columnKey}` : s.columnKey;
}
